import { useState, useEffect, useRef } from 'react'
import { Button } from 'ui/components/Button'
import { ModalButtons } from 'ui/Modal'
import { Spinner } from 'ui/components/Spinner'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import {
  useCancelTerminalRequest,
  useCreateAssociationRequest,
  useTerminalRequest,
} from 'api/TerminalRequests'

import { Trans, useTranslation } from 'react-i18next'
import { TerminalRequestStatusEnum } from '@trybeapp/sdk'

export const WaitingForWearable = ({
  terminal,
  onWearableAssociated,
  onCancel,
  orderId,
  customerId,
}) => {
  const { t } = useTranslation()
  const clientId = customerId
  const notification = useNotificationContext()
  const { mutateAsync: createAssociationRequest, isLoading: isStarting } =
    useCreateAssociationRequest()
  const { mutate: cancelRequest } = useCancelTerminalRequest()
  const [requestId, setRequestId] = useState(null)
  const hasNotifiedRef = useRef(false)

  useEffect(() => {
    if (terminal && !requestId && !isStarting) {
      createAssociationRequest(
        {
          customer_id: clientId,
          terminal_id: terminal.id,
          order_id: orderId,
        },
        {
          onSuccess: (response) => setRequestId(response.data.id),
          onError: (response: Response) => {
            notification.notifyApiError(response)
            onCancel()
          },
        }
      )
    }
  }, [
    terminal,
    requestId,
    createAssociationRequest,
    clientId,
    orderId,
    notification,
    onCancel,
    isStarting,
  ])

  const { data: { data: terminalRequest } = {} } = useTerminalRequest(requestId || '', {
    enabled: !!requestId,
  })

  useEffect(() => {
    if (terminalRequest?.status && !hasNotifiedRef.current) {
      const status = terminalRequest.status

      if (status === TerminalRequestStatusEnum.Succeeded) {
        notification.notify(
          <Notification
            title={t('clients.client_profile.waiting_for_wearable.success_notification.title')}
            description={t(
              'clients.client_profile.waiting_for_wearable.success_notification.description'
            )}
            variant="success"
            autoDismiss
          />
        )
        hasNotifiedRef.current = true
        onWearableAssociated()
      } else if (status === TerminalRequestStatusEnum.Failed) {
        notification.notify(
          <Notification
            title={t('clients.client_profile.waiting.failed_notification.title')}
            description={
              terminalRequest.processor_data.error_message ||
              t('clients.client_profile.waiting_for_wearable.failed_notification.description')
            }
            variant="danger"
          />
        )
        hasNotifiedRef.current = true
        onCancel()
      }
    }
  }, [terminalRequest, onWearableAssociated, notification, onCancel, t])

  const handleCancelRequest = () => {
    if (requestId) {
      cancelRequest(requestId, {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t('clients.client_profile.waiting.cancelled_notification.title')}
              description={t(
                'clients.client_profile.waiting_for_wearable.cancelled_notification.description'
              )}
              variant="info"
              autoDismiss
            />
          )
        },
      })
    }
    onCancel()
  }

  if (isStarting) {
    return (
      <div className="text-center py-8">
        <Spinner />
        <div className="mt-2 font-medium">
          {t('clients.client_profile.waiting.is_starting.description_line_first')}
        </div>
        <div className="text-sm text-gray-500 mt-2">
          <Trans
            i18nKey="clients.client_profile.waiting.is_starting.description_line_second"
            values={{ terminalNameOrId: terminal.name || terminal.id }}
            components={[<span className="font-semibold" />]}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="text-center py-8">
      <Spinner />
      <div className="mt-2 font-medium">
        {t('clients.client_profile.waiting_for_wearable.waiting.description_line_first')}
      </div>
      <div className="text-sm text-gray-500 mt-2">
        <Trans
          i18nKey="clients.client_profile.waiting_for_wearable.waiting.description_line_second"
          values={{ terminalNameOrId: terminal.name || terminal.id }}
          components={[<span className="font-semibold" />]}
        />
      </div>

      <ModalButtons>
        <Button label={t('modal_button.cancel')} variant="primary" onClick={handleCancelRequest} />
      </ModalButtons>
    </div>
  )
}
